// Existencias por bodega. Las mutaciones de stock son atómicas:
//  - producto: RPC atómica (agregar/descontar/ajustar_stock) — inventario + movimiento.
//  - presentacion: se "fabrica"/"deshace" vía Edge Function (consume/devuelve componentes).
import { insforge } from '../insforge';
import { getUsuarioActual } from '../session';
import type { InventarioItem, ItemTipo, NivelStock } from '../../types';

interface InvRow {
  id: string;
  bodega_id: string;
  tipo: ItemTipo;
  ref_id: string;
  stock: number;
}

function nivelDe(stock: number, minimo: number): NivelStock {
  if (stock <= 0) return 'agotado';
  if (minimo > 0 && stock <= minimo) return 'bajo';
  return 'ok';
}

export async function inventarioPorBodega(bodegaId: string): Promise<InventarioItem[]> {
  const [inv, prods, preses] = await Promise.all([
    insforge.database.from('inventario').select('*').eq('bodega_id', bodegaId),
    insforge.database.from('productos').select('id, nombre, foto, unidad, stock_minimo, estado'),
    insforge.database.from('presentaciones').select('id, nombre, foto, estado'),
  ]);
  for (const r of [inv, prods, preses]) if (r.error) throw new Error(r.error.message);

  const mapProd = new Map((prods.data ?? []).map((p: any) => [p.id, p]));
  const mapPres = new Map((preses.data ?? []).map((p: any) => [p.id, p]));

  return ((inv.data ?? []) as InvRow[]).map((r) => {
    const item: any = (r.tipo === 'presentacion' ? mapPres.get(r.ref_id) : mapProd.get(r.ref_id)) ?? {};
    const stock = Number(r.stock);
    const minimo = Number(item.stock_minimo ?? 0);
    return {
      id: r.id,
      bodega_id: r.bodega_id,
      tipo: r.tipo,
      ref_id: r.ref_id,
      nombre: item.nombre ?? '(desconocido)',
      foto: item.foto ?? '',
      unidad: item.unidad ?? '',
      stock,
      stock_minimo: minimo,
      estado: item.estado ?? 'Activo',
      nivel: nivelDe(stock, minimo),
    };
  });
}

export interface OperacionStock {
  bodegaId: string;
  tipo: ItemTipo;
  refId: string;
  cantidad: number;
  motivo?: string;
}

// Suma stock. Para presentaciones = fabricar (Edge Function, consume componentes).
export async function agregarStock(op: OperacionStock): Promise<void> {
  if (op.tipo === 'presentacion') {
    const { error } = await insforge.functions.invoke('fabricar', {
      body: { bodegaId: op.bodegaId, presentacionId: op.refId, cantidad: op.cantidad },
    });
    if (error) throw new Error(error.message);
    return;
  }
  const { error } = await insforge.database.rpc('agregar_stock', {
    p_bodega: op.bodegaId,
    p_tipo: op.tipo,
    p_ref: op.refId,
    p_cantidad: op.cantidad,
    p_usuario: getUsuarioActual(),
    p_motivo: op.motivo ?? '',
  });
  if (error) throw new Error(error.message);
}

// Descuenta stock. Para presentaciones = desfabricar (Edge Function, devuelve componentes).
export async function descontarStock(op: OperacionStock): Promise<void> {
  if (op.tipo === 'presentacion') {
    const { error } = await insforge.functions.invoke('desfabricar', {
      body: { bodegaId: op.bodegaId, presentacionId: op.refId, cantidad: op.cantidad },
    });
    if (error) throw new Error(error.message);
    return;
  }
  const { error } = await insforge.database.rpc('descontar_stock', {
    p_bodega: op.bodegaId,
    p_tipo: op.tipo,
    p_ref: op.refId,
    p_cantidad: op.cantidad,
    p_usuario: getUsuarioActual(),
    p_motivo: op.motivo ?? '',
  });
  if (error) throw new Error(error.message);
}

export interface AjusteStock {
  bodegaId: string;
  tipo: ItemTipo;
  refId: string;
  stock: number; // valor exacto
  motivo?: string;
}

// Fija el stock a un valor exacto con motivo (movimiento 'Ajuste manual').
export async function ajustarStock(op: AjusteStock): Promise<void> {
  const { error } = await insforge.database.rpc('ajustar_stock', {
    p_bodega: op.bodegaId,
    p_tipo: op.tipo,
    p_ref: op.refId,
    p_nuevo: op.stock,
    p_usuario: getUsuarioActual(),
    p_motivo: op.motivo ?? '',
  });
  if (error) throw new Error(error.message);
}
